import type { Reward } from 'types/rewards'
import type { DragEndEvent } from '@dnd-kit/core'

import { DeleteOutlined, EditOutlined, PlusOutlined, FilterOutlined, SettingOutlined } from '@ant-design/icons'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid2 as Grid,
  Menu,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { DndContext, closestCorners, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import AnimateButton from 'components/@extended/AnimateButton'
import Paper from 'components/@extended/Paper'
import { TextField } from 'components/@extended/TextField'
import SearchInput from 'components/SearchInput'
import SimpleTitle from 'components/SimpleTitle'
import ButtonActionBar from 'components/ButtonActionBar/ButtonActionBar'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import useAuth from 'hooks/useAuth'
import useLocalStorage from 'hooks/useLocalStorage'
import {
  useFetchCategoriesWithRewards,
  useCreateRewardCategory,
  useUpdateRewardCategory,
  useCreateReward,
  useUpdateReward,
  useDeleteReward,
  useDeleteRewards,
  invalidateRewardsCache,
} from 'hooks/useRewards'
import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getRootOrganizationId } from 'types/user-profile'
import RewardDialog from './RewardDialog'
import { CategoryCard } from '../components/CategoryCard'

type SortDirection = 'asc' | 'desc'
type SortField = 'name' | 'categoryName' | 'displayOrder'

interface RewardWithCategory extends Reward {
  categoryName: string
  categoryDisplayOrder: number
}

interface CategoryRow {
  id: string
  categoryId: string
  categoryName: string
  categoryDisplayOrder: number
  isCategory: true
  rewardCount: number
  subRows: RewardRow[]
}

interface RewardRow extends RewardWithCategory {
  isCategory: false
  subRows?: never
}

const ManageRewardsSubComponent: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { token, profile } = useAuth()
  const [rootOrganizationId, setRootOrganizationId] = useState<string>('')

  // Filter state with local storage persistence
  const [searchTerm, setSearchTerm] = useLocalStorage<string>('manageRewards-search', '', profile?.user?.id)
  const [selectedCategoryIds, setSelectedCategoryIds] = useLocalStorage<string[]>(
    'manageRewards-categoryFilter',
    [],
    profile?.user?.id
  )
  const [sortField, _setSortField] = useLocalStorage<SortField>(
    'manageRewards-sortField',
    'categoryName',
    profile?.user?.id
  )
  const [sortDirection, _setSortDirection] = useLocalStorage<SortDirection>(
    'manageRewards-sortDirection',
    'asc',
    profile?.user?.id
  )

  // Selection state
  const [selectedRewards, setSelectedRewards] = useState<Set<string>>(new Set())

  // Category filter menu state
  const [categoryFilterAnchorEl, setCategoryFilterAnchorEl] = useState<null | HTMLElement>(null)
  const categoryFilterOpen = Boolean(categoryFilterAnchorEl)

  // Dialog state
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false)
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null)
  const [rewardCategoryId, setRewardCategoryId] = useState<string>('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [rewardToDelete, setRewardToDelete] = useState<{ id: string; name: string } | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false)
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  // Hooks
  const { categoriesWithRewards, isLoading, refetch } = useFetchCategoriesWithRewards(
    rootOrganizationId,
    token!,
    !!rootOrganizationId && !!token
  )
  const { createCategory, isLoading: isCreatingCategory } = useCreateRewardCategory(token!)
  const { updateCategory } = useUpdateRewardCategory(token!)
  const { createReward, isLoading: isCreatingReward } = useCreateReward(token!)
  const { updateReward, isLoading: isUpdatingReward } = useUpdateReward(token!)
  const { deleteReward } = useDeleteReward(token!)
  const { deleteRewards, isLoading: isDeletingRewards } = useDeleteRewards(token!)

  useEffect(() => {
    if (profile) {
      const orgId = getRootOrganizationId(profile)
      if (orgId) {
        setRootOrganizationId(orgId)
      }
    }
  }, [profile])

  // Build flat list of rewards with category info
  const allRewards = useMemo(() => {
    if (!categoriesWithRewards) return []
    return categoriesWithRewards.flatMap((category) =>
      category.rewards.map((reward) => ({
        ...reward,
        categoryName: category.name,
        categoryDisplayOrder: category.displayOrder,
      }))
    )
  }, [categoriesWithRewards])

  // Helper to check if a reward is the primary reward (order 0 in category with order 0)
  const isPrimaryReward = useCallback((reward: { displayOrder?: number; categoryDisplayOrder: number }) => {
    return reward.categoryDisplayOrder === 0 && reward.displayOrder === 0
  }, [])

  // Filter and sort rewards
  const filteredAndSortedRewards = useMemo(() => {
    let rewards = [...allRewards]

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      rewards = rewards.filter(
        (reward) => reward.name.toLowerCase().includes(term) || reward.categoryName.toLowerCase().includes(term)
      )
    }

    // Filter by selected categories
    if (selectedCategoryIds.length > 0) {
      rewards = rewards.filter((reward) => selectedCategoryIds.includes(reward.categoryId))
    }

    // Sort
    rewards.sort((a, b) => {
      let comparison = 0
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name)
          break
        case 'categoryName':
          // Sort by category display order, then by reward display order
          comparison = a.categoryDisplayOrder - b.categoryDisplayOrder
          if (comparison === 0) {
            comparison = (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
          }
          break
        case 'displayOrder':
          comparison = (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
          break
        default:
          comparison = 0
      }
      return sortDirection === 'asc' ? comparison : -comparison
    })

    return rewards
  }, [allRewards, searchTerm, selectedCategoryIds, sortField, sortDirection])

  // Group rewards by category to create hierarchical structure
  const hierarchicalData = useMemo<CategoryRow[]>(() => {
    if (!categoriesWithRewards) return []

    // Group filtered rewards by category
    const rewardsByCategory = new Map<string, RewardRow[]>()

    filteredAndSortedRewards.forEach((reward) => {
      if (!rewardsByCategory.has(reward.categoryId)) {
        rewardsByCategory.set(reward.categoryId, [])
      }
      rewardsByCategory.get(reward.categoryId)!.push({
        ...reward,
        isCategory: false,
      })
    })

    // Create category rows with subRows sorted by displayOrder for drag-and-drop
    const categoryRows: CategoryRow[] = categoriesWithRewards
      .filter((category) => {
        // Only exclude categories if a category filter is applied and this category is not selected
        if (selectedCategoryIds.length > 0 && !selectedCategoryIds.includes(category.id!)) {
          return false
        }
        // Always include the category, even if it has no rewards (allows empty categories to show)
        return true
      })
      .map((category) => {
        const rewards = rewardsByCategory.get(category.id!) || []
        // Always sort subRows by displayOrder for consistent drag-and-drop behavior
        rewards.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))

        return {
          id: `category-${category.id}`,
          categoryId: category.id!,
          categoryName: category.name,
          categoryDisplayOrder: category.displayOrder,
          isCategory: true,
          rewardCount: rewards.length,
          subRows: rewards,
        }
      })

    // Sort categories by display order
    categoryRows.sort((a, b) => a.categoryDisplayOrder - b.categoryDisplayOrder)

    return categoryRows
  }, [categoriesWithRewards, filteredAndSortedRewards, selectedCategoryIds])

  const handleCloseRewardDialog = useCallback(() => {
    setSelectedReward(null)
    setRewardCategoryId('')
    setRewardDialogOpen(false)
  }, [])

  const handleRewardSubmit = useCallback(
    async (data: any) => {
      try {
        if (selectedReward) {
          await updateReward(selectedReward.id!, data)
          setSnackbar({ open: true, message: 'Reward updated successfully', severity: 'success' })
        } else {
          await createReward(data)
          setSnackbar({ open: true, message: 'Reward created successfully', severity: 'success' })
        }
        handleCloseRewardDialog()
        invalidateRewardsCache(rootOrganizationId, token!)
        refetch()
      } catch (error) {
        setSnackbar({ open: true, message: 'Failed to save reward', severity: 'error' })
      }
    },
    [selectedReward, createReward, updateReward, handleCloseRewardDialog, rootOrganizationId, token, refetch]
  )

  // Selection handlers
  const handleToggleReward = useCallback((rewardId: string) => {
    setSelectedRewards((prev) => {
      const next = new Set(prev)
      if (next.has(rewardId)) {
        next.delete(rewardId)
      } else {
        next.add(rewardId)
      }
      return next
    })
  }, [])

  const handleDeleteSelectedRewards = useCallback(() => {
    setBulkDeleteConfirmOpen(true)
  }, [])

  const handleConfirmBulkDelete = useCallback(async () => {
    try {
      await deleteRewards(Array.from(selectedRewards))
      setSnackbar({
        open: true,
        message: `${selectedRewards.size} rewards deleted successfully`,
        severity: 'success',
      })
      setSelectedRewards(new Set())
      invalidateRewardsCache(rootOrganizationId, token!)
      refetch()
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete rewards', severity: 'error' })
    } finally {
      setBulkDeleteConfirmOpen(false)
    }
  }, [selectedRewards, deleteRewards, rootOrganizationId, token, refetch])

  const handleCancelBulkDelete = useCallback(() => {
    setBulkDeleteConfirmOpen(false)
  }, [])

  const handleEditSelectedRewards = useCallback(() => {
    // Get the selected reward data and pass via navigation state
    const selectedRewardData = allRewards.filter((r) => selectedRewards.has(r.id!))
    navigate('/rewards/edit', { state: { rewards: selectedRewardData } })
  }, [selectedRewards, allRewards, navigate])

  const handleClearSelection = useCallback(() => {
    setSelectedRewards(new Set())
  }, [])

  // Handler for adding rewards to a category
  const handleAddRewardToCategory = useCallback((categoryId: string) => {
    setSelectedReward(null)
    setRewardCategoryId(categoryId)
    setRewardDialogOpen(true)
  }, [])

  // Handler for editing a reward
  const handleRewardEdit = useCallback(
    (rewardId: string) => {
      // Find the reward in hierarchicalData
      for (const category of hierarchicalData) {
        const reward = category.subRows.find((r) => r.id === rewardId)
        if (reward) {
          setSelectedReward(reward)
          setRewardCategoryId(reward.categoryId)
          setRewardDialogOpen(true)
          return
        }
      }
    },
    [hierarchicalData]
  )

  // Handler for deleting a reward
  const handleDeleteReward = useCallback(
    (rewardId: string) => {
      // Find the reward in hierarchicalData to get its name
      for (const category of hierarchicalData) {
        const reward = category.subRows.find((r) => r.id === rewardId)
        if (reward) {
          setRewardToDelete({ id: rewardId, name: reward.name })
          setDeleteConfirmOpen(true)
          return
        }
      }
    },
    [hierarchicalData]
  )

  // Handler for confirming delete
  const handleConfirmDelete = useCallback(async () => {
    if (!rewardToDelete) return

    try {
      await deleteReward(rewardToDelete.id)
      setSnackbar({ open: true, message: 'Reward deleted successfully', severity: 'success' })
      invalidateRewardsCache(rootOrganizationId, token!)
      await refetch()
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete reward', severity: 'error' })
    } finally {
      setDeleteConfirmOpen(false)
      setRewardToDelete(null)
    }
  }, [rewardToDelete, deleteReward, rootOrganizationId, token, refetch])

  // Handler for canceling delete
  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false)
    setRewardToDelete(null)
  }, [])

  // Category dialog handlers
  const handleAddCategory = useCallback(() => {
    setNewCategoryName('')
    setCategoryDialogOpen(true)
  }, [])

  const handleCloseCategoryDialog = useCallback(() => {
    setNewCategoryName('')
    setCategoryDialogOpen(false)
  }, [])

  const handleCreateCategory = useCallback(async () => {
    if (!newCategoryName.trim()) {
      setSnackbar({ open: true, message: 'Category name is required', severity: 'error' })
      return
    }

    try {
      // Get the next display order (highest + 1)
      const maxDisplayOrder = categoriesWithRewards?.reduce((max, cat) => Math.max(max, cat.displayOrder), -1) ?? -1
      const nextDisplayOrder = maxDisplayOrder + 1

      await createCategory({
        rootOrganizationId,
        organizationId: rootOrganizationId,
        name: newCategoryName.trim(),
        displayOrder: nextDisplayOrder,
      })

      setSnackbar({ open: true, message: 'Category created successfully', severity: 'success' })
      handleCloseCategoryDialog()
      invalidateRewardsCache(rootOrganizationId, token!)
      await refetch()
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to create category', severity: 'error' })
    }
  }, [
    newCategoryName,
    categoriesWithRewards,
    createCategory,
    rootOrganizationId,
    token,
    refetch,
    handleCloseCategoryDialog,
  ])

  // Search handler
  const handleSearch = useCallback(
    (value: string) => {
      setSearchTerm(value)
    },
    [setSearchTerm]
  )

  // Category filter handlers
  const handleToggleCategoryFilter = useCallback(
    (categoryId: string) => {
      setSelectedCategoryIds((prev) => {
        if (prev.includes(categoryId)) {
          return prev.filter((id) => id !== categoryId)
        } else {
          return [...prev, categoryId]
        }
      })
    },
    [setSelectedCategoryIds]
  )

  const handleClearCategoryFilter = useCallback(() => {
    setSelectedCategoryIds([])
  }, [setSelectedCategoryIds])

  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Unified drag handler for both categories and rewards
  const handleUnifiedDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || active.id === over.id) {
        return
      }

      // Check if dragging a category
      const isDraggingCategory = hierarchicalData.some((cat) => cat.id === active.id)

      if (isDraggingCategory) {
        // Handle category drag
        const categories = [...hierarchicalData]
        const oldIndex = categories.findIndex((cat) => cat.id === active.id)
        const newIndex = categories.findIndex((cat) => cat.id === over.id)

        if (oldIndex === -1 || newIndex === -1) {
          return
        }

        // Don't allow moving the first category (displayOrder === 0)
        if (categories[oldIndex].categoryDisplayOrder === 0) {
          return
        }

        // Don't allow moving to the first position if there's a category with displayOrder 0
        if (newIndex === 0 && categories[0].categoryDisplayOrder === 0) {
          return
        }

        // Reorder the array
        const reordered = [...categories]
        const [removed] = reordered.splice(oldIndex, 1)
        reordered.splice(newIndex, 0, removed)

        // Update all category displayOrders
        try {
          for (let i = 0; i < reordered.length; i++) {
            const category = reordered[i]
            await updateCategory(category.categoryId, { displayOrder: i })
          }

          invalidateRewardsCache(rootOrganizationId, token!)
          await refetch()
        } catch (error) {
          setSnackbar({ open: true, message: 'Failed to update category order', severity: 'error' })
        }
      } else {
        // Handle reward drag
        await handleRewardDragLogic(event)
      }
    },
    [hierarchicalData, updateCategory, rootOrganizationId, token, refetch]
  )

  // Handle drag end to reorder rewards within or across categories
  const handleRewardDragLogic = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event

      if (!over) return

      // Find source category
      let sourceCategoryId: string | null = null
      let draggedReward: RewardRow | null = null

      for (const cat of hierarchicalData) {
        const found = cat.subRows.find((r) => r.id === active.id)
        if (found) {
          sourceCategoryId = cat.categoryId
          draggedReward = found
          break
        }
      }

      if (!sourceCategoryId || !draggedReward) return

      // Determine target category
      let targetCategoryId: string | null = null
      let isDroppedOnDroppableZone = false

      // Case 1: Dropped on another reward
      if (!String(over.id).startsWith('category-droppable-')) {
        for (const cat of hierarchicalData) {
          const found = cat.subRows.find((r) => r.id === over.id)
          if (found) {
            targetCategoryId = cat.categoryId
            break
          }
        }
      }
      // Case 2: Dropped on empty category zone
      else {
        isDroppedOnDroppableZone = true
        targetCategoryId = String(over.id).replace('category-droppable-', '')
      }

      if (!targetCategoryId) return

      // If dropped on the droppable zone of the same category, ignore (no-op)
      if (sourceCategoryId === targetCategoryId && isDroppedOnDroppableZone) {
        return
      }

      // Don't allow moving the primary reward (Salary) from the primary category to other categories
      if (
        sourceCategoryId !== targetCategoryId &&
        draggedReward.categoryDisplayOrder === 0 &&
        draggedReward.displayOrder === 0
      ) {
        setSnackbar({
          open: true,
          message: 'The primary reward cannot be moved to other categories',
          severity: 'error',
        })
        return
      }

      // Same category - just reorder
      if (sourceCategoryId === targetCategoryId && over.id !== active.id) {
        const category = hierarchicalData.find((cat) => cat.categoryId === sourceCategoryId)
        if (!category) return

        const rewards = [...category.subRows]
        const oldIndex = rewards.findIndex((r) => r.id === active.id)
        const newIndex = rewards.findIndex((r) => r.id === over.id)

        if (oldIndex === -1 || newIndex === -1) return

        const reordered = [...rewards]
        const [removed] = reordered.splice(oldIndex, 1)
        reordered.splice(newIndex, 0, removed)

        try {
          for (let i = 0; i < reordered.length; i++) {
            await updateReward(reordered[i].id!, { displayOrder: i })
          }
          invalidateRewardsCache(rootOrganizationId, token!)
          await refetch()
        } catch (error) {
          setSnackbar({ open: true, message: 'Failed to update reward order', severity: 'error' })
        }
      }
      // Different category - move and reorder
      else if (sourceCategoryId !== targetCategoryId) {
        const targetCategory = hierarchicalData.find((cat) => cat.categoryId === targetCategoryId)
        if (!targetCategory) return

        const targetRewards = [...targetCategory.subRows]
        let insertIndex = targetRewards.length

        if (!String(over.id).startsWith('category-droppable-')) {
          const overIndex = targetRewards.findIndex((r) => r.id === over.id)
          if (overIndex !== -1) insertIndex = overIndex
        }

        try {
          // Update moved reward
          await updateReward(draggedReward.id!, {
            categoryId: targetCategoryId,
            displayOrder: insertIndex,
          })

          // Reorder target category rewards
          targetRewards.splice(insertIndex, 0, draggedReward)
          for (let i = 0; i < targetRewards.length; i++) {
            if (targetRewards[i].id !== draggedReward.id) {
              await updateReward(targetRewards[i].id!, { displayOrder: i })
            }
          }

          // Reorder source category rewards
          const sourceCategory = hierarchicalData.find((cat) => cat.categoryId === sourceCategoryId)
          if (sourceCategory) {
            const sourceRewards = sourceCategory.subRows.filter((r) => r.id !== draggedReward.id)
            for (let i = 0; i < sourceRewards.length; i++) {
              await updateReward(sourceRewards[i].id!, { displayOrder: i })
            }
          }

          invalidateRewardsCache(rootOrganizationId, token!)
          await refetch()
        } catch (error) {
          setSnackbar({ open: true, message: 'Failed to move reward', severity: 'error' })
        }
      }
    },
    [hierarchicalData, updateReward, rootOrganizationId, token, refetch]
  )

  // Check if the primary reward is in the selection
  const selectionIncludesPrimaryReward = useMemo(() => {
    return Array.from(selectedRewards).some((id) => {
      const reward = allRewards.find((r) => r.id === id)
      return reward && isPrimaryReward(reward)
    })
  }, [selectedRewards, allRewards, isPrimaryReward])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Grid container pb={2}>
        <Grid size={{ xs: 12 }}>
          <SimpleTitle title="Manage Rewards" />
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={3} alignItems="center">
        <Grid size={{ xs: 3 }}>
          <SearchInput
            id="rewards-search"
            onSearch={handleSearch}
            initialValue={searchTerm}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 9 }} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FilterOutlined />}
            onClick={(e) => setCategoryFilterAnchorEl(e.currentTarget)}
            sx={{ height: '40px' }}
          >
            Categories {selectedCategoryIds.length > 0 ? `(${selectedCategoryIds.length})` : ''}
          </Button>
          <Menu
            anchorEl={categoryFilterAnchorEl}
            open={categoryFilterOpen}
            onClose={() => setCategoryFilterAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { minWidth: '250px', maxHeight: '400px', mt: 1 } }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Filter by Category
                </Typography>
                {selectedCategoryIds.length > 0 && <Button onClick={handleClearCategoryFilter}>Clear</Button>}
              </Box>
              {categoriesWithRewards?.map((category) => (
                <FormControlLabel
                  key={category.id}
                  control={
                    <Checkbox
                      checked={selectedCategoryIds.includes(category.id!)}
                      onChange={() => handleToggleCategoryFilter(category.id!)}
                    />
                  }
                  label={`${category.name} (${category.rewards.length})`}
                  sx={{
                    display: 'flex',
                    width: '100%',
                    '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
                  }}
                />
              ))}
              {(!categoriesWithRewards || categoriesWithRewards.length === 0) && (
                <Typography color="textSecondary" variant="body2">
                  No categories available
                </Typography>
              )}
            </Box>
          </Menu>
        </Grid>
      </Grid>

      {hierarchicalData.length === 0 ? (
        <Paper sx={{ mb: 3, p: 4 }}>
          <Box textAlign="center">
            <Typography color="textSecondary">
              {searchTerm || selectedCategoryIds.length > 0 ? 'No rewards match your filters' : 'No rewards yet'}
            </Typography>
          </Box>
        </Paper>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleUnifiedDragEnd}>
          <SortableContext
            items={[
              ...hierarchicalData.map((cat) => cat.id),
              ...hierarchicalData.flatMap((cat) => cat.subRows.map((r) => r.id!)),
            ]}
            strategy={verticalListSortingStrategy}
          >
            {hierarchicalData.map((categoryRow) => {
              const isDraggable = categoryRow.categoryDisplayOrder !== 0

              return (
                <CategoryCard
                  key={categoryRow.id}
                  category={categoryRow}
                  isDraggable={isDraggable}
                  selectedRewards={selectedRewards}
                  onToggleRewardSelect={handleToggleReward}
                  onRewardEdit={handleRewardEdit}
                  onRewardDelete={handleDeleteReward}
                  onAddReward={handleAddRewardToCategory}
                  theme={theme}
                />
              )
            })}
          </SortableContext>
        </DndContext>
      )}

      {/* Action buttons under table */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button variant="contained" startIcon={<PlusOutlined />} onClick={handleAddCategory}>
          Add Category
        </Button>
        <Button variant="outlined" startIcon={<SettingOutlined />} onClick={() => navigate('/rewards/categories')}>
          Edit Categories
        </Button>
        <AnimateButton>
          <Button variant="outlined" startIcon={<PlusOutlined />} onClick={() => navigate('/rewards/create')}>
            Create Rewards
          </Button>
        </AnimateButton>
      </Box>

      {/* Bulk action bar */}
      <ButtonActionBar show={selectedRewards.size > 0} maxWidth="900px" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <Box sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
            {selectedRewards.size} reward{selectedRewards.size > 1 ? 's' : ''} selected
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button variant="outlined" onClick={handleClearSelection}>
            Cancel
          </Button>
          <Button variant="outlined" color="primary" startIcon={<EditOutlined />} onClick={handleEditSelectedRewards}>
            Edit Selected
          </Button>
          <Tooltip title={selectionIncludesPrimaryReward ? 'Cannot delete the primary reward' : ''}>
            <span>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteOutlined />}
                onClick={handleDeleteSelectedRewards}
                disabled={isDeletingRewards || selectionIncludesPrimaryReward}
              >
                {isDeletingRewards ? 'Deleting...' : 'Delete Selected'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </ButtonActionBar>

      {/* Dialogs */}
      <RewardDialog
        open={rewardDialogOpen}
        onClose={handleCloseRewardDialog}
        onSubmit={handleRewardSubmit}
        reward={selectedReward}
        categoryId={rewardCategoryId}
        categories={categoriesWithRewards}
        isLoading={isCreatingReward || isUpdatingReward}
        organizationId={rootOrganizationId}
      />

      <ConfirmDialog
        show={deleteConfirmOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Delete Reward"
        message={`Are you sure you want to delete "${rewardToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
        cancelColor="warning"
      />

      <ConfirmDialog
        show={bulkDeleteConfirmOpen}
        onConfirm={handleConfirmBulkDelete}
        onCancel={handleCancelBulkDelete}
        title="Delete Multiple Rewards"
        message={`Are you sure you want to delete ${selectedRewards.size} selected reward${selectedRewards.size > 1 ? 's' : ''}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
        cancelColor="warning"
      />

      {/* Add Category Dialog */}
      <Dialog open={categoryDialogOpen} onClose={handleCloseCategoryDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              autoFocus
              label="Category Name *"
              variant="outlined"
              pill
              shrink
              fullWidth
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newCategoryName.trim()) {
                  handleCreateCategory()
                }
              }}
              placeholder="Enter category name"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <AnimateButton>
            <Button
              variant="contained"
              color="warning"
              onClick={handleCloseCategoryDialog}
              disabled={isCreatingCategory}
            >
              Cancel
            </Button>
          </AnimateButton>
          <AnimateButton>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateCategory}
              disabled={isCreatingCategory || !newCategoryName.trim()}
            >
              {isCreatingCategory ? 'Creating...' : 'Create Category'}
            </Button>
          </AnimateButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}

const ManageRewards: React.FC = () => {
  return (
    <Suspense fallback={<CircularProgress />}>
      <ManageRewardsSubComponent />
    </Suspense>
  )
}

export default ManageRewards
